import type { AspectPreset, ExportFormat, ExportPreset, SlidePreset } from './types';

/**
 * The size and format tables.
 *
 * These used to live in `engine/export.ts` next to the canvas exporter, which
 * put a DOM dependency underneath `slice.ts` and `doc/defaults.ts` by way of
 * one import. They are plain data, so they live here and every platform's
 * exporter reads them.
 */

export const ASPECT_PRESETS: AspectPreset[] = [
  { id: '1:1', name: 'Square', ratio: 1, group: 'square' },
  { id: '4:5', name: 'Portrait', ratio: 4 / 5, group: 'social' },
  { id: '9:16', name: 'Story', ratio: 9 / 16, group: 'social' },
  { id: '16:9', name: 'Wide', ratio: 16 / 9, group: 'screen' },
  { id: '3:2', name: 'Photo', ratio: 3 / 2, group: 'print' },
  { id: '2:3', name: 'Photo ↕', ratio: 2 / 3, group: 'print' },
  { id: '4:3', name: 'Classic', ratio: 4 / 3, group: 'screen' },
  { id: '210:297', name: 'A4', ratio: 210 / 297, group: 'print' },
  { id: '297:210', name: 'A4 ↔', ratio: 297 / 210, group: 'print' },
];

/**
 * Shapes a single splitter tile can take. A profile grid renders every post as
 * a square, so 1:1 is the default; 4:5 buys more feed height at the cost of a
 * centre crop on the profile page.
 */
export const TILE_ASPECTS: AspectPreset[] = [
  { id: '1:1', name: 'Square', ratio: 1, group: 'square' },
  { id: '4:5', name: 'Portrait', ratio: 4 / 5, group: 'social' },
  { id: '3:4', name: 'Classic ↕', ratio: 3 / 4, group: 'social' },
  { id: '9:16', name: 'Story', ratio: 9 / 16, group: 'social' },
];

/**
 * Exact store-ready slide sizes. These are absolute pixel dimensions rather
 * than ratios because the app stores expect them literally — exporting at 1×
 * produces a file that uploads without a resize.
 */
export const SLIDE_PRESETS: SlidePreset[] = [
  { id: 'ig-portrait', name: 'Instagram 4:5', w: 1080, h: 1350, group: 'social' },
  { id: 'ig-square', name: 'Instagram 1:1', w: 1080, h: 1080, group: 'social' },
  { id: 'story', name: 'Story / Reels', w: 1080, h: 1920, group: 'social' },
  { id: 'ios-6.7', name: 'iPhone 6.7"', w: 1290, h: 2796, group: 'ios' },
  { id: 'ios-6.5', name: 'iPhone 6.5"', w: 1242, h: 2688, group: 'ios' },
  { id: 'ipad-12.9', name: 'iPad 12.9"', w: 2048, h: 2732, group: 'ios' },
  { id: 'play-phone', name: 'Play Store phone', w: 1080, h: 1920, group: 'android' },
];

export const EXPORT_PRESETS: ExportPreset[] = [
  { id: 'web', name: 'Web', longEdge: 1600, note: 'Fast to share' },
  { id: 'instagram', name: 'Instagram', longEdge: 1440, note: 'Feed & stories' },
  { id: 'hi', name: 'High', longEdge: 3000, note: 'Retina & crops' },
  { id: 'print', name: 'Print', longEdge: 4960, note: '≈300 dpi at A4' },
];

export const FORMAT_INFO: Record<
  ExportFormat,
  { label: string; mime: string; ext: string; alpha: boolean }
> = {
  png: { label: 'PNG', mime: 'image/png', ext: 'png', alpha: true },
  jpeg: { label: 'JPEG', mime: 'image/jpeg', ext: 'jpg', alpha: false },
  webp: { label: 'WebP', mime: 'image/webp', ext: 'webp', alpha: true },
};
